import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';

import { Money } from '@bpos';
import { OfferSource } from '@cds';
import { OptionStatus, Participant, ParticipantPriceType, ParticipantStatus, Visibility } from '@cds/betting-offer';
import { MessageEnvelope, MessageType } from '@cds/push';
import { OptionMarketUpdateCommand } from '@cds/push/fixture-commands';
import { ConnectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import {
    CdsPushService,
    GameSubscription,
    OptionMarketSubscription,
    ParticipantMarketSubscription,
    isGameDeleteEnvelope,
    isGameUpdateEnvelope,
    isOptionMarketDeleteEnvelope,
    isOptionMarketUpdateEnvelope,
    isParticipantUpdateEnvelope,
} from '@frontend/sports/content-distribution/feature';
import { ColumnLayoutService } from '@frontend/sports/layout/feature';
import { ModalDialogService } from '@frontend/sports/modal/feature';
import { ModelPopupTypes } from '@frontend/sports/navigation/core/feature/url-helper';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';

import { MyBetsBetSlip } from '../../../my-bets-base/models/my-bets-viewmodels';
import { PopupManager } from '../../../popup/popup-manager.service';
import { BetslipConnectorService } from './betslip-connector.service';
import { ClearBetslipComponent } from './clear-betslip.component';
import { ReBetCalculatorService } from './re-bet-calculator.service';
import { ReBetPicksService } from './re-bet-picks.service';
import { ReBetAvailabilityService } from './re-bet-visibility.service';
import { IReBetPicksPayload } from './re-bet.models';

@Component({
    selector: 'ms-re-bet',
    templateUrl: 're-bet.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ReBetComponent implements OnChanges {
    @Input() betslip: MyBetsBetSlip;
    @Output() setFlash = new EventEmitter<boolean>();

    private allPicks: IReBetPicksPayload = ReBetPicksService.getDefaultReBetPicks();
    possibleWinnings = 0;

    constructor(
        private siteCore: Sitecore,
        private rebetAvailabilityService: ReBetAvailabilityService,
        private changeDetector: ChangeDetectorRef,
        private connection: ConnectionConfig,
        private cdsPush: CdsPushService,
        private betslipConnector: BetslipConnectorService,
        private modalDialog: ModalDialogService,
        private calculator: ReBetCalculatorService,
        private pickFactory: ReBetPicksService,
        private popupManager: PopupManager,
        private columnLayout: ColumnLayoutService,
        private userService: UserService,
        private userSettings: SyncUserSettingsService,
    ) {}

    @HostBinding('class.re-bet-hidden') get hiddenClass(): boolean {
        return this.picksAmount === 0;
    }

    async ngOnChanges(changes: SimpleChanges) {
        const betslip = changes['betslip'];

        if (betslip && betslip.currentValue.reBetPicks) {
            this.allPicks = await this.pickFactory.createPicks(betslip.currentValue);
            this.emitFlash();

            this.subscribe();
            this.calculateWinnings();
        }
    }

    onAddToBetslip(): void {
        const canBeAdded = this.betslipConnector.canBeAdded(this.calculator.getPicksLength(this.allPicks));

        if (!canBeAdded) {
            const modalDialogRef = this.modalDialog.openDialog(ClearBetslipComponent, { settings: { cssClass: 'clear-betslip-dialog' } });
            modalDialogRef.result.finally(() => this.dialogClosed());
        } else {
            this.betslipConnector.addToBetslip(this.allPicks);
            this.showBetslip();
        }
        this.changeDetector.markForCheck();
    }

    private emitFlash(): void {
        if (this.betslip.listType === BetslipListType.Settled) {
            this.setFlash.emit(this.picksAmount !== 0);
        }
    }

    get picksAmount(): number {
        return this.calculator.getPicksLength(this.allPicks);
    }

    get getStake(): number {
        return this.userSettings.bettingSettings.defaultStake || this.betslip.stake.value;
    }

    get getMoney(): Money {
        return this.userSettings.bettingSettings.defaultStake
            ? {
                  currency: this.userService.currency,
                  value: this.userSettings.bettingSettings.defaultStake!,
              }
            : this.betslip.stake;
    }

    private showBetslip(): void {
        if (this.columnLayout.layout.isRightVisible) {
            return;
        }
        if (this.popupManager.isPopupOpen(ModelPopupTypes.MyBets) && this.popupManager.isPopupInStack(ModelPopupTypes.Betslip)) {
            this.popupManager.close();
        } else {
            this.popupManager.open(ModelPopupTypes.Betslip);
        }
    }

    private dialogClosed(): boolean {
        const canBeAdded = this.betslipConnector.canBeAdded(this.calculator.getPicksLength(this.allPicks));

        if (canBeAdded) {
            this.betslipConnector.addToBetslip(this.allPicks);
            this.showBetslip();
        }

        return true;
    }

    private async calculateWinnings(): Promise<void> {
        this.possibleWinnings = await this.calculator.getPossibleWinnings(this.allPicks, this.getStake);
        this.changeDetector.markForCheck();
    }

    private gameFixtureUpdated = (m: MessageEnvelope) => {
        let fixtures = this.allPicks.fixtures;

        if (isGameDeleteEnvelope(m)) {
            fixtures = fixtures.filter((f) => f.id !== m.payload.fixtureId);
        }

        if (isGameUpdateEnvelope(m)) {
            const cdsGame = m.payload.game;

            for (const result of cdsGame.results) {
                const pick = fixtures.find((f) => f.optionGroups[0].options[0].id === result.id);

                if (!pick) {
                    continue;
                }

                pick.optionGroups[0].options[0].nativePrice = NativePrice.fromNativePrice(result);
                pick.optionGroups[0].options[0].online = result.visibility === Visibility.Visible && result.odds > 1;
            }
        }

        this.calculateWinnings();
    };

    private optionMarketFixtureUpdated = (m: MessageEnvelope) => {
        let fixtures = this.allPicks.fixtures;

        if (isOptionMarketDeleteEnvelope(m)) {
            fixtures = fixtures.filter((f) => f.id !== m.payload.fixtureId);
        }

        if (m.messageType === MessageType.FixtureUpdate) {
            return;
        }

        if (isOptionMarketUpdateEnvelope(m)) {
            const market = (<OptionMarketUpdateCommand>m.payload).optionMarket;

            for (const result of market.options) {
                const pick = fixtures.find((f) => f.optionGroups[0].options[0].id === result.id);

                if (!pick) {
                    continue;
                }

                pick.optionGroups[0].options[0].nativePrice = NativePrice.fromNativePrice(result.price);
                pick.optionGroups[0].options[0].online = !!(result.status === OptionStatus.Visible && result.price.odds && result.price.odds > 1);

                this.calculateWinnings();
            }
        }

        this.changeDetector.markForCheck();
    };

    private participantUpdated = (m: MessageEnvelope) => {
        if (!this.calculator.getPicksLength(this.allPicks)) {
            return;
        }

        if (isParticipantUpdateEnvelope(m)) {
            const picks = [...this.allPicks.winGameFixtures, ...this.allPicks.xCastFixtures];

            const selectedPicks = picks.filter((p) => p.fixtureId === m.payload.fixtureId);

            for (const pick of selectedPicks) {
                const pickParticipant = pick.participants.find((p) => p.participantId === m.payload.participant.id)!;
                this.updateFromParticipantWin(pickParticipant, m.payload.participant, pick.fixtureId);
            }
        }

        this.changeDetector.markForCheck();
    };

    private updateFromParticipantWin(participant: WinGameParticipant, updateParticipant: Participant, fixtureId: string): void {
        participant.status = updateParticipant.status || ParticipantStatus.NotDefined;

        for (const option of participant.options) {
            const updateOption = updateParticipant.options.find((o) => o.id === option.id);

            if (!updateOption || updateOption.priceType !== ParticipantPriceType.Fixed) {
                continue;
            }

            option.price = updateOption.price;
        }
    }

    private subscribe(): void {
        if (!this.calculator.getPicksLength(this.allPicks)) {
            return;
        }

        const lang = this.connection.culture;

        for (const fixture of this.allPicks.fixtures) {
            const subscription =
                fixture.offerSource === OfferSource.V1 && fixture.hybridFixtureData == null
                    ? new GameSubscription(fixture.offerContext, fixture.id, fixture.optionGroups[0].id, lang, this.gameFixtureUpdated)
                    : new OptionMarketSubscription(
                          fixture.offerContext,
                          fixture.hybridFixtureData != null ? fixture.hybridFixtureData.fixtureId : fixture.id,
                          fixture.optionGroups[0].id,
                          lang,
                          this.optionMarketFixtureUpdated,
                      );

            this.cdsPush.subscribe(subscription);
        }

        for (const pick of [...this.allPicks.winGameFixtures, ...this.allPicks.xCastFixtures]) {
            for (const p of pick.participants) {
                const participantSubscription = new ParticipantMarketSubscription(
                    pick.contexts,
                    pick.fixtureId,
                    p.participantId.toString(),
                    lang,
                    this.participantUpdated,
                );

                this.cdsPush.subscribe(participantSubscription);
            }
        }
    }

    get description(): string {
        return this.betslip.listType === BetslipListType.Open
            ? this.siteCore.reBet.messages.CtaDescriptionForOpenTab
            : this.siteCore.reBet.messages.CtaDescriptionForSettledTab;
    }

    get descriptionEnabled(): boolean {
        return this.rebetAvailabilityService.isCTASubHeaderAvailable(this.betslip.listType);
    }
}
